import { useState, useEffect, useCallback, useRef } from 'react';
import { formatISO, setHours, setMinutes, setSeconds, getHours, getMinutes } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import { useParams, useHistory } from 'react-router-dom';
import _ from 'lodash';

import 'react-day-picker/dist/style.css';

// Socket
import { socket } from '../../misc/socket'

// components
import Analytics from '../../components/analytics/analytics'
import DeviceIndicator from '../../components/deviceIndicator/deviceIndicator';

// TODO segment
// import Segment from '../../components/segment/segment'

// Hooks 
import {  useAppDispatch, useAppSelector } from '../../misc/hooks'

// Actions
import {getInitialState, abtestGet, abtestUpdate, abtestDelete, abtestGetAnalytics, getThemes, generateToken } from '../../slice/abtest'
import {  getAppInstallValidity } from '../../slice/shopifyStore'

// common
import Switch from "../../components/_common/ui/switch/switch";
import { Dropdown, IDropdownOption } from "../../components/_common/ui/dropdown/dropdown";

// icon
import IconEdit from "../../assets/icons/icon_edit";
import IconTrash from "../../assets/icons/icon_trash";

// misc
import { Iabtest } from '../../misc/interface/abtest'
import { Itest } from '../../misc/interface/test';

// utils
import { generateUUID, addDays} from '../../misc/utils'

// Interface
import { IShopifyStore } from '../../slice/shopifyStore'

export interface ILaunchTime {
	hour: {value: number, label: string}, 
	minute: {value: number, label: string}, 
}
export interface Itheme {
	name: string,
	id: number
}

export interface Ierrors {
	theme?: boolean,
	no_recipe?: boolean
}

function getObjectHour (i: number) {
	return i === 0 ? {value: 0, type: 'hour', label: '12 am'} : {value: i, type: 'hour', label: i < 12 ? `${i} am` : `${i === 12 ? 12 : i - 12} pm`}
}

function getObjectMinute (minutes: number) {
	return {value: minutes, label: `${minutes === 0 ? '00' : minutes} mn`, type: 'minute'}
}

function Edit() {
	const history = useHistory();
	const { abtest_id } = useParams<{abtest_id: string}>();
	const dispatch = useAppDispatch()

	const shopifyStore: IShopifyStore | null = useAppSelector((state) => state.shopifyStore.shopifyStore)

	// State
	const [isLoading, setIsLoading] = useState<Boolean>(true)
	const [isLoadingDelete, setIsLoadingDelete] = useState<Boolean>(false)
	const [isLoadingStatus, setIsLoadingStatus] = useState<Boolean>(false)
	const [isLoadingEditor, setIsLoadingEditor] = useState<Boolean>(false)
	const [abtest, setAbtest] = useState<Iabtest>(getInitialState())
	const [analytics, setAnalytics] = useState<any>()
	const [themes, setThemes] = useState<Array<Itheme>>()
	const [errors, setErrors] = useState<Ierrors>()
	const [deleteStep, setDeleteStep] = useState<string>('')

	// Ref
	const isEnableUpdateAbtest = useRef<boolean >(false);

	// isUserHasPaid
	const isPaidUser = shopifyStore?.currentAppInstallation?.activeSubscriptions?.some((s: any) => s.status === 'ACTIVE') 

	// If need to upgrade
	const isUpgrade = ['draft', 'scheduled'].includes(abtest?.status) && shopifyStore?.myshopifyDomain && !shopifyStore?.currentAppInstallation?.activeSubscriptions?.some((s: any) => s.status === 'ACTIVE') 

	// Constant, !t.theme_id, to also the default control
	const testLength = abtest?.test.filter(t => !t.theme_id || (t.theme_id === abtest.theme.id) )?.length

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedUpdateAbtest = useCallback(
		_.debounce(() => {
			dispatch(abtestUpdate({abtest, abtest_id}));
		}, 500),
		[dispatch, abtest] 
	);

	const checkExtensionOnTheme = useCallback(
		async () => {
			let res = await dispatch(getAppInstallValidity(abtest.theme.id))
	
			if (res?.payload) {
				setErrors(prevState => ({
					...prevState,
					theme: !res?.payload?.valid
				}));
			}
		},
		[abtest.theme, dispatch] 
	);
	
	// / UseEffect to load the abtest, connect the socket
	useEffect(() => {
		const launch = async () => {
			// If the url contains the id of the abtest
			if (abtest_id) {
				// load the abtest
				let res: any = await dispatch(abtestGet(abtest_id))

				if (res?.payload) {
					// Retrieve the themes of the store
					let resTheme = await dispatch(getThemes())

					let newThemes: Array<Itheme> = resTheme?.payload?.data?.themes?.nodes || []

					if (newThemes?.length) {
						setThemes(newThemes)
					}

					let newAbtest: Iabtest = res.payload

					if (!newAbtest?.theme?.id && newThemes?.length) {
						const mainTheme = newThemes.find( (t: any) => t.role === 'MAIN')
						newAbtest.theme.id = `${mainTheme?.id || newThemes[0].id}`
						newAbtest.theme.label = mainTheme?.name || newThemes[0].name
					}
					// disable abtest update when loading the abtest for the first time
					isEnableUpdateAbtest.current = false
					setAbtest({...newAbtest})
					document.title = `A/B test - ${newAbtest?.name || abtest_id}`

					// If status is live, load analytics
					if (['paused', 'live', 'completed'].includes(newAbtest.status)) {
						let resAnalytics = await dispatch(abtestGetAnalytics(abtest_id))

						if (resAnalytics?.payload) {
							setAnalytics(resAnalytics?.payload)
						}
					}

				} else {
					history.push('/')
				}

				setIsLoading(false)
				// TODO : manage case socket error : first case OnConnect in the useeffet, 2nd case when adding a listener below
				// no-op if the socket is already connected
				socket.connect();

				// when updating the server code, It's necessary to refresh the page of the app, because the connection is lost
				// (No reconnection in dev mode)
				socket.on(abtest_id, (data) => {
					isEnableUpdateAbtest.current = false
					
					setAbtest(prevState => {
						if (prevState.status === "live" || prevState.status === "completed") {
							return prevState;
						}

						return {
							...prevState,
							test: data
						};
					});
					
				});	

			}
		}

		launch()

		// Scroll to top
		window.scrollTo(0,0)
		
		return () => {
		  	socket.disconnect();
			document.title = 'Lyntest'
		};

	}, [dispatch, abtest_id, history]);

	// UseEffect to handle the update of the abtest
	useEffect(() => {
		if (isEnableUpdateAbtest?.current) {
			debouncedUpdateAbtest();
		} else {
			isEnableUpdateAbtest.current = true
		}
	
		return () => {
		  debouncedUpdateAbtest.cancel();
		};
	}, [abtest, debouncedUpdateAbtest]);

	// Check If the extension is installed on the theme
	useEffect(() => {
		if (abtest?.theme?.id) {
			checkExtensionOnTheme()
		}
	}, [abtest.theme, checkExtensionOnTheme, dispatch]);

	// Functions
	const handleChange = (property: keyof Iabtest, value: any) => {
		let newState = value, newStateToMerge: any = {}

		// Test
		if (['test'].includes(property)) {
			let test: Array<Itest> = abtest.test

			// Add a new test 
			if (value.type === 'variation-new') {

				const id = crypto?.randomUUID ? crypto.randomUUID() : generateUUID()

				const defaultTraffic =  test?.[0]?.traffic?.value ? 100 - test[0].traffic.value : 10

				test.push({
					name: 'Variant',
					recipe: null,
					recipe_live: null,
					traffic: {value: defaultTraffic , label: `${defaultTraffic}%`},
					id: id,
					theme_id: abtest.theme.id
				})
				newState = test

			// Delete a test
			} else if (value.type === 'variation-delete') {
				newState = test.filter(t => t.id !== value.id)

			// Update traffic 
			} else if (value.type === 'traffic') {
				newState = test.map(t => {
					// Set the value and label for all variation OR only control
					if ((t.id && value.id) || (!value.id && !t.id)) {
						t.traffic = {value: value.value, label: value.label}
					}

					// Rebalance the traffic for control, If we update variation
					if (!t.id && value.id && ((value.value + t.traffic.value) !== 100) ) {
						t.traffic = {value: 100 - value.value, label: `${100 - value.value} %`}
					}

					// Rebalance the traffic for all variation, If we update control
					if (t.id && !value.id && ((value.value + t.traffic.value) !== 100)) {
						t.traffic = {value: 100 - value.value, label: `${100 - value.value} %`}
					}
					return t
				})
			} 

		// scheduled_at and launch time
		} else if (property === 'scheduled_at') {
			if (newState.type && ['hour', 'minute'].includes(newState.type)) {

				if (newState.type === 'hour') newState = formatISO(setHours(new Date(abtest.scheduled_at), newState.value))
				if (newState.type === 'minute') newState = formatISO(setMinutes(new Date(abtest.scheduled_at), newState.value))

			} else {
				// The datepicker reset the hours and minutes, so need to add them each time we change the date
				if (newState) {

					// Condition on abtest.scheduled_at , due to when user switch the schedule for the first time
					const hour = abtest.scheduled_at ? getHours(new Date(abtest.scheduled_at)) : 0
					const minute = abtest.scheduled_at? getMinutes(new Date(abtest.scheduled_at)) : 0

					newState = formatISO(setSeconds(setMinutes(setHours(new Date(newState), hour), minute), 0))
				}
			}

			newStateToMerge['end_at'] = newState ? formatISO(addDays(newState, abtest.duration.value)) : formatISO(addDays(Date.now(), abtest.duration.value))
		} 
		
		newStateToMerge[property] = newState
		setAbtest(prevState => ({
			...prevState,
			...newStateToMerge
		}));
	}

	const handleDuration = (option: IDropdownOption) => {
		const value = option.value as number
		setAbtest(prevState => ({
			...prevState,
			[option.type as keyof ILaunchTime]: {label: option.label, value: value},
			end_at: abtest?.scheduled_at ? formatISO(addDays(abtest.scheduled_at, value)) : formatISO(addDays(Date.now(), value))
		}));

	}

	const handleOpenEditor = async (id: string) => {
		setIsLoadingEditor(true)
		if (shopifyStore?.primaryDomain && id && abtest_id) {
			const res = await dispatch(generateToken({var_id: id, abtest_id}))
			const token = res?.payload?.t
			
			if (token) {
				const width = window.screen.width 
				const height = window.screen.height 
				
				// Parameter &pb=0 disable the preview bar
				window.open(
					`https://${shopifyStore.primaryDomain}?preview_theme_id=${abtest.theme.id.replace('gid://shopify/OnlineStoreTheme/', '')}&lyn-token=${token}&pb=0`,
					'lyntestWidget',
					`width=${width},height=${height},resizable=yes,scrollbars=yes`
				);
			}
		}
		setIsLoadingEditor(false)
	}
	
	const updateStatusAbtest = async () => {
		setIsLoadingStatus(true)

		let status = abtest?.status 
		if (status === 'draft') {
			status = abtest.scheduled_at ? 'scheduled' : 'live'

		} else if (status === 'scheduled') {
			status = 'draft'

		} else if (status === 'live') {
			status = 'paused'

		} else if (status === 'paused') {
			status = 'live'
		}

		// If status to live or scheduled, but not recipe_live 
		if (['live', 'scheduled'].includes(status) && !abtest?.test?.[1]?.recipe_live) {
			setErrors(prevState => ({
				...prevState,
				no_recipe: true
			}));
			setTimeout(() => {
				setErrors(prevState => ({
					...prevState,
					no_recipe: false
				}));
			}, 5000);
			setIsLoadingStatus(false)
			return
		}

		let res = await dispatch(abtestUpdate({ abtest: {...abtest, status: status}, abtest_id}))

		if (res?.payload?.result === true) {
			isEnableUpdateAbtest.current = false
			handleChange('status', status)

			if (['paused', 'live'].includes(status)) {
				let resAnalytics = await dispatch(abtestGetAnalytics(abtest_id))

				if (resAnalytics?.payload) {
					setAnalytics(resAnalytics?.payload)
				}
				window.scrollTo({top: 0, behavior: "smooth"});
			}
		}
		setIsLoadingStatus(false)
	}

	const deleteAbtest = async () => {
		setIsLoadingDelete(true)
		if (!deleteStep) {
			setDeleteStep('confirm')
			setIsLoadingDelete(false)
			return
		}

		let res = await dispatch(abtestDelete({abtest_id}))

		if (res?.payload?.deleted === true) history.push('/')

		setIsLoadingDelete(false)
	}

	const buildDurationsOptions = () => {
		let options = []
		for (let i = 1; i < 31; i++) {
			options.push( 
				{value: i, type: 'duration', label: `${i} day${ i > 1? 's' : '' }`}
			)
		}
		return options
	}

	const buildHoursOptions = () => {
		let options = []
		for (let i = 0; i < 24; i++) {
			options.push(getObjectHour(i))
		}
		return options
	}

	const buildMinutesOptions = () => {
		let options = []
		let minutes = 0
		for (let i = 0; i < 4; i++) {
			options.push(getObjectMinute(minutes))
			minutes += 15
		}
		return options
	}

	const buildTrafficOptions = (id: string) => {
		let options = []
		let traffic = 10
		for (let i = 0; i < 9; i++) {
			options.push( 
				{value: traffic, label: `${traffic} %`, type: 'traffic', id: id}
			)
			traffic += 10
		}
		return options
	}

	const buildThemesOptions = () => {
		let options = []
		if (themes && themes.length) {
			for (let theme of themes) {
				options.push( 
					{id: `${theme.id}`, label: `${theme.name}`}
				)
			}
		}		
		return options
	}

	const getLabelBtnStatus = () => {
		let label = '', status = abtest?.status 
		if (status === 'draft') {
			label = abtest.scheduled_at ? 'Launch scheduled test' : 'Launch test'

		} else if (status === 'scheduled') {
			label = 'Set as draft'

		} else if (status === 'live') {
			label = 'Pause test'

		} else if (status === 'paused') {
			label = 'Resume test'
		}
		return label	
	}

	const hasChanges = (v: any) => !!v && typeof v === 'object' && Object.keys(v).length > 0;

	// Un device est "présent" dès qu'une seule URL a au moins une modification dessus.
	const getDevices = (recipeLive: any) => {
		let desktop = false;
		let mobile = false;
		for (const page of Object.values<any>(recipeLive || {})) {
			if (!desktop && hasChanges(page?.desktop)) desktop = true;
			if (!mobile && hasChanges(page?.mobile)) mobile = true;
			if (desktop && mobile) break;
		}
		return { desktop, mobile };
	}

	const devices = getDevices(abtest?.test?.[1]?.recipe_live);

	return (
		<div className="edit">
			{/* // TODO <Segment /> */}

			{ !isLoading?
				<div className="edit__status">
					Status : 
					<span className={`edit__status-current ${abtest?.status}`}>
							{abtest?.status || ''}
					</span>
				</div>
			:''}
			<div className="edit__container">
				{ !isLoading?
					<>
						{['live', 'completed', 'paused'].includes(abtest?.status) && analytics? 
							<Analytics analytics={analytics} status={abtest.status}/>

						: ['live', 'completed', 'paused'].includes(abtest?.status) && shopifyStore?.myshopifyDomain && !isPaidUser? 
							<a 
								className="edit__btn-inactive-plan"
								href={`https://admin.shopify.com/store/${shopifyStore?.myshopifyDomain?.replace('.myshopify.com','')}/charges/lyntest/pricing_plans`} 
								target="_blank" rel="noopener noreferrer"
							>
								Upgrade your plan to view analytics, then refresh this page.
							</a>
						:
							''}

						<div className="edit__title">
							Edit your test
						</div>

						<div className="edit__bloc">
							{/* abtest name */}
							<div className="edit__subbloc">
								<p className="edit__label">
									A/B test name :
								</p>
								<input className="edit__input" type="text" placeholder="Choose a name for your A/B test" value={abtest.name} onChange={ e => handleChange('name', e.target.value)} />
							</div>

							{/*ab test duration */}
							<div className={`edit__subbloc ${abtest?.status === 'completed' ? 'disable' : ''}`}>
								<p className="edit__label">
									A/B test duration :
								</p>
								<div className="edit__dropdown duration">
									<Dropdown options={buildDurationsOptions()} currentValue={abtest?.duration?.label} selectOption={handleDuration}/>
								</div>
							</div>

							{/* End date */}
							<div className="edit__subbloc">
								<p className="edit__label">
									End date : {new Date(abtest?.end_at)?.toLocaleString() || ''}
								</p>
							</div>

						</div>


						{/* schedule */}
						<div className="edit__bloc">
							<div className={`edit__subbloc flex aligns-center ${['paused','live', 'completed'].includes(abtest?.status) ? 'disable' : '' }`}>
								<p className="edit__label">
									Schedule :
								</p>
								<div onClick={() => { abtest && abtest.scheduled_at ? handleChange('scheduled_at', '') : handleChange('scheduled_at', formatISO(new Date()))}}>
									<Switch value={abtest && abtest.scheduled_at ? true : false} color='green' />
								</div>
								
							</div>

							{abtest && abtest.scheduled_at && (
								<>
									<div className={`edit__subbloc ${['paused','live', 'completed'].includes(abtest?.status) ? 'disable' : '' }`}>
										<p className="edit__label">
											Launch date :
										</p>
										
										<DayPicker
											mode="single"
											selected={new Date(abtest.scheduled_at)}
											onSelect={(s) => {handleChange('scheduled_at', s ? formatISO(s) : '')}}
											defaultMonth={new Date(abtest.scheduled_at)}
										/>
									</div>

									<div className={`edit__subbloc ${['paused','live', 'completed'].includes(abtest?.status) ? 'disable' : '' }`}>
										<p className="edit__label">
											Launch time :
										</p>
										<div className="edit__launch-time">
											<div className="edit__launch-hour">
												<Dropdown options={buildHoursOptions()} currentValue={getObjectHour(getHours(new Date(abtest.scheduled_at))).label} selectOption={(o: keyof ILaunchTime) => handleChange('scheduled_at', o)}/>
											</div>
											<div className="edit__launch-minute">
												<Dropdown options={buildMinutesOptions()} currentValue={getObjectMinute(getMinutes(new Date(abtest.scheduled_at))).label} selectOption={(o: keyof ILaunchTime) => handleChange('scheduled_at', o)}/>
											</div>
										</div>
									</div>

									{/* End date */}
									<div className="edit__subbloc">
										<p className="edit__label">
											End date : {new Date(abtest?.end_at)?.toLocaleString() || ''}
										</p>
									</div>
								</>
							)}
						</div>


						{/* theme */}
						<div className="edit__bloc">
							<div className={`edit__subbloc ${['paused','live', 'completed'].includes(abtest?.status) ? 'disable' : '' }`}>
								<p className="edit__label">
									Theme
								</p>
								<div className="edit__theme">
									<Dropdown options={buildThemesOptions()} currentValue={abtest.theme.label} selectOption={(v: keyof Iabtest)=>{handleChange('theme', v)}} />
								</div>

								{/* Error in case the extension is not install on the theme*/}
								{ shopifyStore?.myshopifyDomain && errors?.theme ? 
									<div className="edit__theme-error">
										<a className="edit__theme-error-link" href={`https://${shopifyStore.myshopifyDomain}/admin/themes/${abtest.theme.id.replace('gid://shopify/OnlineStoreTheme/', '')}/editor?context=apps&activateAppId=${process.env.REACT_APP_SHOPIFY_LYNTEST_THEME_EXTENSION_ID}/app-embed`} title="Link to install the extension" target="_blank" rel="noopener noreferrer">
											Install the extension on your theme using this link
										</a>
										<button className="edit__theme-error-btn" onClick={() => { checkExtensionOnTheme()}}>
											Check the install
										</button>
									</div>
								: ''}
									
							</div>
						</div>

						{/* variation */}
						<div className="edit__bloc variation">
							<div className="edit__subbloc">
								<div className="edit__variations">
									{testLength ?
										abtest.test.filter(t => !t.theme_id || (t.theme_id === abtest.theme.id)).map((test, k) => {
											return (
												<div className="edit__variation" key={test.id}>
													<div className="edit__variation-container">
														<div className="edit__variation-label">
															{test.name}
														</div>
														{test.name !== 'Control' ? 
															<div className="edit__variation-bloc">
																<button className="edit__variation-btn-edit" onClick={() => handleOpenEditor(test.id)}>
																	<IconEdit />

																	{ isLoadingEditor ? 
																		<div className="edit__load tiny" >
																			<div className="edit__loader tiny"></div>
																		</div>
																	:''}
																</button>

																{!['live', 'completed'].includes(abtest?.status) ?
																	<button className="edit__variation-btn-delete" onClick={ () => handleChange('test', {type: 'variation-delete', id : test.id})}>
																		<IconTrash />
																	</button>
																:''}

																{['live', 'completed'].includes(abtest?.status) ? 
																	<span className="edit__variation-edit-info">
																		When the status is '{abtest?.status}', you can view your test but cannot edit or delete it.
																	</span>
																:''}
																
															</div>
														: ''}
													</div>
													<div className={`edit__dropdown traffic ${['live', 'completed'].includes(abtest?.status) ? 'disable' : ''}`}>
														<Dropdown options={buildTrafficOptions(test.id)} currentValue={test.traffic.label} selectOption={(v: keyof Iabtest)=>{handleChange('test', v)}} placeholder="traffic"/>
													</div>
												</div>
											)
										})
									:''}
									
									{testLength < 2 ? 
										<button className="edit__btn-variation" onClick={ () => handleChange('test', {type: 'variation-new'})}>
											Add variant 
										</button> 
									: ''}
									
								</div>

								{testLength > 1 ? 
									<DeviceIndicator desktop={devices.desktop} mobile={devices.mobile} />	
								: ''}						
							</div>
						</div>

						{/* segment */}
						{/* <div className="edit__bloc">
							<div className="edit__subbloc">
								<p className="edit__label">
									Segment
								</p>
							</div>
							<button className="edit__btn-segment">
								Add segment
							</button>
						</div> */}


						{/* Upgrade plan link */}
						{ isUpgrade ?
							<a 
								className="edit__btn-upgrade"
								href={`https://admin.shopify.com/store/${shopifyStore?.myshopifyDomain?.replace('.myshopify.com','')}/charges/lyntest/pricing_plans`} 
								target="_blank" rel="noopener noreferrer"
							>
								Start your 14-day free trial to launch this test. You won’t be charged until the trial ends. <br/>
								Once subscribed, come back and refresh this page.
							</a>
						: ''}

						{/* Button  status  */}
						{abtest?.status !== 'completed' ?
							<button className={`edit__btn-status ${isUpgrade ? 'disable' : ''}`} onClick={updateStatusAbtest}> 
								{getLabelBtnStatus()} 

								{ isLoadingStatus ?
									<div className="edit__load tiny" >
										<div className="edit__loader tiny"></div>
									</div>
								: ''}
							</button>
						: ''}

						{ errors?.no_recipe ? 
							<div className="edit__launch-error">
								No changes detected. Please update the variant before launching the test.
							</div>
						: ''}

						{/* Button  Delete  */}
						<button className={`edit__btn-delete ${!deleteStep ? 'light' : ''}`} onClick={deleteAbtest}> 
							{!deleteStep ? 'Delete test' : 'Click again to confirm'} 
							{ isLoadingDelete ?
								<div className="edit__load tiny" >
									<div className="edit__loader tiny red"></div>
								</div>
							:''}
						</button>
					</>
				: 
					<div className="edit__load" >
						<div className="edit__loader"></div>
					</div>
				}
			</div>
		</div> 
	);
}

export default Edit;
